// As fontes da "Arte editável": o espelho de `api/src/modules/design/dominio/arte.ts`
// (FONTES) no orren-agents. Os arquivos em `public/design/fontes` são os
// MESMOS de `api/fontes` lá — o servidor desenha com eles, o editor mostra com
// eles, e o teste `arte-bate-com-o-servidor` confere byte a byte, peso a peso
// e as métricas.
//
// `ascendente` e `descendente` (do `hhea`, em frações do em) são o que põe a
// linha no lugar: o navegador não serve de régua para a altura da linha (no
// Windows ele usa as métricas `win` de algumas fontes, e a Bebas Neue mede
// 950/350 lá e 900/300 no Mac). Por isso o editor posiciona cada linha pela
// linha de base, com estes números — a mesma conta do servidor.
export type CategoriaDaFonte = 'sem-serifa' | 'serifa' | 'display' | 'manuscrita' | 'da-marca';

export interface Fonte { id: string; nome: string; categoria: CategoriaDaFonte; pesos: readonly number[]; ascendente: number; descendente: number }

export const FONTES: readonly Fonte[] = [
  { id: 'inter', nome: 'Inter', categoria: 'sem-serifa', pesos: [400, 500, 600, 700, 800, 900], ascendente: 0.9688, descendente: 0.2412 },
  { id: 'montserrat', nome: 'Montserrat', categoria: 'sem-serifa', pesos: [400, 500, 600, 700, 800, 900], ascendente: 0.968, descendente: 0.251 },
  { id: 'poppins', nome: 'Poppins', categoria: 'sem-serifa', pesos: [300, 400, 500, 600, 700, 800, 900], ascendente: 1.05, descendente: 0.35 },
  { id: 'roboto', nome: 'Roboto', categoria: 'sem-serifa', pesos: [300, 400, 500, 700, 900], ascendente: 0.9277, descendente: 0.2441 },
  { id: 'open-sans', nome: 'Open Sans', categoria: 'sem-serifa', pesos: [400, 600, 700, 800], ascendente: 1.0688, descendente: 0.293 },
  { id: 'lato', nome: 'Lato', categoria: 'sem-serifa', pesos: [400, 700, 900], ascendente: 0.987, descendente: 0.213 },
  { id: 'raleway', nome: 'Raleway', categoria: 'sem-serifa', pesos: [400, 600, 700, 800, 900], ascendente: 0.94, descendente: 0.234 },
  { id: 'nunito', nome: 'Nunito', categoria: 'sem-serifa', pesos: [400, 600, 700, 800, 900], ascendente: 1.011, descendente: 0.353 },
  { id: 'space-grotesk', nome: 'Space Grotesk', categoria: 'sem-serifa', pesos: [400, 500, 700], ascendente: 0.984, descendente: 0.292 },
  { id: 'oswald', nome: 'Oswald', categoria: 'display', pesos: [400, 500, 600, 700], ascendente: 1.193, descendente: 0.289 },
  { id: 'bebas-neue', nome: 'Bebas Neue', categoria: 'display', pesos: [400], ascendente: 0.9, descendente: 0.3 },
  { id: 'anton', nome: 'Anton', categoria: 'display', pesos: [400], ascendente: 1.1763, descendente: 0.3291 },
  { id: 'archivo-black', nome: 'Archivo Black', categoria: 'display', pesos: [400], ascendente: 0.878, descendente: 0.21 },
  { id: 'barlow-condensed', nome: 'Barlow Condensed', categoria: 'display', pesos: [500, 700, 800], ascendente: 1, descendente: 0.2 },
  { id: 'playfair-display', nome: 'Playfair Display', categoria: 'serifa', pesos: [400, 600, 700, 800, 900], ascendente: 1.082, descendente: 0.251 },
  { id: 'lora', nome: 'Lora', categoria: 'serifa', pesos: [400, 600, 700], ascendente: 1.006, descendente: 0.274 },
  { id: 'merriweather', nome: 'Merriweather', categoria: 'serifa', pesos: [400, 700, 900], ascendente: 0.984, descendente: 0.273 },
  { id: 'dm-serif-display', nome: 'DM Serif Display', categoria: 'serifa', pesos: [400], ascendente: 1.036, descendente: 0.335 },
  { id: 'dancing-script', nome: 'Dancing Script', categoria: 'manuscrita', pesos: [400, 700], ascendente: 0.92, descendente: 0.28 },
  { id: 'pacifico', nome: 'Pacifico', categoria: 'manuscrita', pesos: [400], ascendente: 1.303, descendente: 0.453 },
  { id: 'great-vibes', nome: 'Great Vibes', categoria: 'manuscrita', pesos: [400], ascendente: 0.851, descendente: 0.401 },
  { id: 'caveat', nome: 'Caveat', categoria: 'manuscrita', pesos: [400, 700], ascendente: 0.96, descendente: 0.3 },
];

export const CATEGORIAS_DAS_FONTES: { id: CategoriaDaFonte; rotulo: string }[] = [
  { id: 'sem-serifa', rotulo: 'Sem serifa' }, { id: 'display', rotulo: 'Títulos' }, { id: 'serifa', rotulo: 'Com serifa' }, { id: 'manuscrita', rotulo: 'Manuscrita' },
];

export const NOME_DO_PESO: Record<number, string> = {
  100: 'Fina', 200: 'Extraleve', 300: 'Leve', 400: 'Normal', 500: 'Média', 600: 'Seminegrito', 700: 'Negrito', 800: 'Extranegrito', 900: 'Preta',
};

/**
 * As fontes que a loja enviou (Marca): "loja-" + 16 hex. Não moram em
 * public/design/fontes — vêm de /api/fontes, com as métricas que o servidor
 * leu do arquivo (a mesma conta da linha de base). Um arquivo, um peso.
 */
export interface FonteDaLoja extends Fonte { url: string; formato: 'ttf' | 'otf' | 'woff'; italico: boolean }
export const ehFonteDaLoja = (id: string) => /^loja-[0-9a-f]{16}$/.test(id);
const DA_LOJA = new Map<string, FonteDaLoja>();

/** O que a API devolve (GET .../estudio/marca/fontes) vira fonte do editor. */
export function registrarFontesDaLoja(lista: readonly { id: string; nome: string; formato: 'ttf' | 'otf' | 'woff'; peso: number; italico: boolean; ascendente: number; descendente: number; url: string }[]): void {
  for (const f of lista) {
    DA_LOJA.set(f.id, { id: f.id, nome: f.nome, categoria: 'da-marca', pesos: [f.peso], ascendente: f.ascendente, descendente: f.descendente, url: f.url, formato: f.formato, italico: f.italico });
  }
}
export const fontesDaLoja = (): FonteDaLoja[] => [...DA_LOJA.values()];

/**
 * O id a partir do que a Marca guarda (espelho de `idDaFonte` no servidor):
 * o id, uma fonte da loja, ou o nome — as Marcas de antes guardavam o nome
 * digitado ("Playfair Display"). Nome fora do catálogo → null.
 */
export function idDaFonte(v: string | null | undefined): string | null {
  const s = String(v ?? '').trim();
  if (!s) return null;
  if (ehFonteDaLoja(s)) return s;
  const f = FONTES.find((x) => x.id === s || x.nome.toLowerCase() === s.toLowerCase());
  return f ? f.id : null;
}

export const fonteDe = (id: string): Fonte => DA_LOJA.get(id) ?? FONTES.find((f) => f.id === id) ?? FONTES[0];

/** O peso que o arquivo tem mais perto do pedido (espelho de `pesoMaisProximo` no servidor). */
export function pesoMaisProximo(fonte: Fonte, peso: number): number {
  return fonte.pesos.reduce((melhor, p) => (Math.abs(p - peso) < Math.abs(melhor - peso) ? p : melhor), fonte.pesos[0]);
}

export const arquivoDaFonte = (id: string, peso: number) => {
  const daLoja = DA_LOJA.get(id);
  if (daLoja) return `${daLoja.id}.${daLoja.formato}`;
  const f = fonteDe(id);
  return `${f.id}-${pesoMaisProximo(f, peso)}.woff`;
};

/** Cada arquivo vira uma família própria no navegador: assim ele nunca "inventa" um negrito por cima do normal. */
export const familiaDaFonte = (id: string, peso: number) => `arte-${arquivoDaFonte(id, peso).replace(/.(woff|ttf|otf)$/, '')}`;

/** O painel mora em /app: o que está em `public/` sai em /app/... (o `base` do Vite), em desenvolvimento e no Hosting. */
const PASTA_DAS_FONTES = `${import.meta.env.BASE_URL}design/fontes/`;
const carregadas = new Map<string, Promise<void>>();

const FORMATO_CSS = { ttf: 'truetype', otf: 'opentype', woff: 'woff' } as const;
/** De onde o navegador baixa: a pasta do painel (catálogo) ou /api/fontes (as da loja, na mesma origem). */
function origemDaFonte(id: string, peso: number): string {
  const daLoja = DA_LOJA.get(id);
  return daLoja ? `url(${daLoja.url}) format('${FORMATO_CSS[daLoja.formato]}')` : `url(${PASTA_DAS_FONTES}${arquivoDaFonte(id, peso)}) format('woff')`;
}

/** Carrega o arquivo da fonte uma vez (o editor espera antes de mostrar, senão o texto pula de fonte). */
export function carregarFonte(id: string, peso: number): Promise<void> {
  const familia = familiaDaFonte(id, peso);
  let p = carregadas.get(familia);
  if (!p) {
    p = typeof FontFace === 'undefined' || typeof document === 'undefined' || !document.fonts
      ? Promise.resolve()
      : new FontFace(familia, origemDaFonte(id, peso)).load()
        .then((f) => { document.fonts.add(f); })
        .catch(() => { carregadas.delete(familia); });
    carregadas.set(familia, p);
  }
  return p;
}
